module Meevo.Components.BaseClasses {

    export interface IPagedResultsColumns {
        scrollColumns: number,
        checkScrollColumns: () => void,
        desktop?: MediaQueryList,
        tablet?: MediaQueryList,
        mobile?: MediaQueryList,
    }

    export class PagedResults<T, TSearch extends Model.SearchCriteria> implements IDataMaintenanceInfiniteList {
        private endpoint: string;
        private suppressRetrieval: boolean;
        private numItems: number;
        private dataService: IDataService;
        private loadedPages: any;
        private requestPage: Model.SearchPage<T, TSearch>;
        private requestCount: Model.SearchCount<TSearch>;
        private retrievalInProgress: boolean;
        private countIsValidated: boolean;
        private additional: any;
        private scrollColumnsObj: IPagedResultsColumns;
        private scrollColumns: number;
        private firstCallMade: boolean = false;

        constructor(dataService: IDataService, endpoint: string, private callbackOnPageLoad?: Function, private context?: any, scrollColumnsObj?: IPagedResultsColumns, private callbackOnResults?: Function, private requireCount: boolean = false) {
            this.endpoint = endpoint;
            this.suppressRetrieval = true;
            this.firstCallMade = false;
            this.numItems = 0;
            this.dataService = dataService;
            this.loadedPages = [];
            this.requestPage = <Model.SearchPage<T, TSearch>>{};
            this.requestCount = <Model.SearchCount<TSearch>>{};
            if (scrollColumnsObj) {
                this.scrollColumnsObj = scrollColumnsObj;
                this.scrollColumns = scrollColumnsObj.scrollColumns;
            }
        }

        /**
         * Clears the current search results.
         */
        clearSearch(): void {
            this.firstCallMade = false;
            this.numItems = 0;
            this.loadedPages = [];
        }

        changeEndpoint(endpoint): void {
            this.endpoint = endpoint;
        }

        /**
       * Initializes a new search with the supplied request.
       * @param {Model.ItemPage<TRequestType>} request
       */
        initSearch(request: Model.SearchPage<T, TSearch>, fallbackCriteria?: any, rootCriteria: Object = {}): ng.IPromise<any> {
            this.firstCallMade = true;
            this.loadedPages = [];
            this.requestPage = _.extend(request, rootCriteria);
            this.requestPage.pageNumber = 0;
            this.requestCount = angular.copy(rootCriteria);
            this.requestCount.criteria = this.requestPage.criteria;
            this.requestCount.asLocationId = this.requestPage.asLocationId;
            this.suppressRetrieval = false;
            this.retrievalInProgress = false;
            this.countIsValidated = true;

            if (this.requireCount) {
                this.numItems = 0;
                return this.getCount();
            }
            else {
                this.numItems = 1;
                var deferred = this.dataService.Promise.defer();
                deferred.resolve(this.numItems);
                return deferred.promise;
            }
        }

        /**
        * Handles getting the item at the requested index *OR* loading more data if necessary.
        * Required by MD to implement infinite scroll.
        * @param {number} index
        * @return {TResponseType} data element at speicifed index.
        */
        getItemAtIndex(index: number): T {
            this.isColumnsCountChanged();

            var itemPageCount = this.scrollColumns ? (this.requestPage.itemsPerPage / this.scrollColumns) : this.requestPage.itemsPerPage;
            var pageNumber = Math.floor(index / itemPageCount);
            var page = this.loadedPages[pageNumber];

            if (page) {
                let itemPage = <T>page[index % itemPageCount];

                if (itemPage) {
                    return itemPage;
                }
            } else if (page !== null) {
                this.fetchMoreItems(pageNumber);
            }
        }

        /** 
        * Required by MD to implement infinite scroll.
        * @return {number} total # of items.
        */
        getLength(): number {
            if (!this.requireCount) {
                if (!this.firstCallMade) {
                    return 0;
                }
                if (!this.retrievalInProgress && !this.countIsValidated) {
                    var itemsPerPage = (this.requestPage && this.requestPage.itemsPerPage ? this.requestPage.itemsPerPage : 0);
                    var lastPageCount = null, pageNo = this.loadedPages.length - 1;
                    for (; pageNo >= 0; pageNo--) {
                        if (this.loadedPages[pageNo].length > 0) {
                            lastPageCount = this.loadedPages[pageNo].length;
                            break;
                        }
                    }
                    this.numItems = (Math.max(0, pageNo) * itemsPerPage) + (lastPageCount == null ? 0 : lastPageCount);
                    if ((this.loadedPages.length - 1) == pageNo && this.loadedPages[this.loadedPages.length - 1].length == itemsPerPage) {
                        this.numItems += 1;
                    }
                    this.countIsValidated = true;
                }
            }
            return this.scrollColumns ? Math.ceil(this.numItems / this.scrollColumns) : this.numItems;
        }

        /**
         * Gets a total count of all the records that match the request criteria.
         */
        private getCount(): ng.IPromise<any> {
            this.retrievalInProgress = true;
            return this.dataService.getCount(this.endpoint, this.requestCount).then((results: any) => {
                if (typeof (results) == "object") {
                    this.numItems = results.count;
                    this.additional = results;
                } else {
                    this.numItems = results;
                }
                this.countIsValidated = true;
                this.loadedPages = new Array(Math.ceil(this.numItems / this.requestPage.itemsPerPage));
                return results;
            }).catch(() => {
                this.countIsValidated = false;
            }).finally(() => {
                this.retrievalInProgress = false;
            });
        }

        private getInitialCount(): ng.IPromise<any> {
            this.retrievalInProgress = true;
            return this.dataService.getCount(this.endpoint, this.requestCount).then((results: any) => {
                if (typeof (results) == "object") {
                    this.numItems = results.count;
                    this.additional = results;
                } else {
                    this.numItems = results;
                }
                this.countIsValidated = this.numItems > 0;
                return results;
            }).catch(() => {
                this.countIsValidated = false;
            });
        }

        /**
        * Loads data from the data service...  Handles applying filters and paging.
        * Results get loaded into 'loadedPages' which the UI should bind to.
        * @param {number} pageNumber
        */
        private fetchMoreItems(pageNumber: number): void {
            if ((!this.requireCount || this.countIsValidated) && !this.retrievalInProgress && !this.suppressRetrieval) {
                if (!this.requireCount) {
                    this.countIsValidated = false;
                }

                this.retrievalInProgress = true;
                this.loadedPages[pageNumber] = null;

                this.requestPage.pageNumber = pageNumber;
                if (this.requestPage.criteria && !_.isUndefined(this.requestPage.criteria['pageNumber'])) {
                    this.requestPage.criteria['pageNumber'] = pageNumber;
                }

                this.dataService.getListPaged(this.endpoint, this.requestPage).then((results: T[]) => {
                    this.loadedPages[pageNumber] = [];

                    if (this.scrollColumns) {
                        this.setLoadedPage(pageNumber, results);
                    } else {
                        for (let i = 0; i < results.length; i++) {
                            this.loadedPages[pageNumber].push(results[i]);
                        }
                        if (_.isFunction(this.callbackOnResults)) {
                            this.loadedPages[pageNumber] = this.callbackOnResults(this.numItems, this.loadedPages[pageNumber]);
                        }
                    }

                    if (this.callbackOnPageLoad && !this.context) {
                        this.callbackOnPageLoad(pageNumber, this.loadedPages[pageNumber]);
                    } else if (this.callbackOnPageLoad && this.context) {
                        this.callbackOnPageLoad(pageNumber, this.loadedPages[pageNumber], this.context);
                    }


                }).finally(() => {
                    this.retrievalInProgress = false;
                });
            }
        }

        /**
         * Check is columns count is changed and reformat all loaded pages
         */
        isColumnsCountChanged() {
            if (!this.scrollColumnsObj) { return }

            this.scrollColumnsObj.checkScrollColumns();

            if (this.scrollColumnsObj.scrollColumns !== this.scrollColumns) {
                this.scrollColumns = this.scrollColumnsObj.scrollColumns;

                _.each(this.loadedPages, (item: Array<any>, index) => {
                    if (!item) return;
                    if (_.isArray(item)) {
                        item = _.flatten(item, true);
                    }
                    this.setLoadedPage(index, item);
                });
            }
        }

        /**
         * Format loadedPages array to array of arrays for columns view
         * @param {number} pageNumber
         * @param {Array} list
         */
        public setLoadedPage(pageNumber: number, list: Array<any>) {
            this.loadedPages[pageNumber].length = 0;

            for (let i = 0; i < list.length; i++) {
                let row = list.slice(i, i + this.scrollColumns);
                if (row) {
                    this.loadedPages[pageNumber].push(row);
                }
                i += this.scrollColumns - 1;
            }
        }

        public getAllItems() {
            return (<any[]>this.loadedPages).reduce((all, current) => {
                all = all.concat(current);

                return all;
            }, []);
        }
    }
}
